import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Plus, Send, Pause, X, BarChart3, Edit, Play } from 'lucide-react';
import CreateCampaignModal from './CreateCampaignModal';
import { useWebSocket } from '../../hooks/useWebSocket';
import { apiClient } from '../../services/api';
import ConfirmDialog, { useConfirmDialog } from '../ui/ConfirmDialog';

interface CampaignsProps {
  wabaAccountId: string;
  qualityRating?: string;
  accountStatus?: string;
}

interface Campaign {
  id: string;
  name: string;
  description?: string;
  status: string;
  type: string;
  totalRecipients: number;
  sentCount: number;
  deliveredCount: number;
  failedCount: number;
  createdAt: string;
  scheduledAt?: string;
  templateName?: string;
  templateLanguage?: string;
  wabaAccountId?: string;
}

interface Template {
  id: string;
  name: string;
  language: string;
  status: string;
  components: any[];
}

export default function Campaigns({ wabaAccountId, qualityRating, accountStatus }: CampaignsProps) {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const { confirm: showConfirm, dialogProps } = useConfirmDialog();

  // Verificar se a qualidade está bloqueada (LOW/RED)
  const isQualityBlocked = qualityRating?.toLowerCase() === 'low' || qualityRating?.toLowerCase() === 'red';
  const accStatus = accountStatus?.toUpperCase();
  const isAccountBlocked = accStatus === 'BANNED' || accStatus === 'BLOCKED';
  const isDispatchBlocked = isQualityBlocked || isAccountBlocked;
  const blockMessage = isAccountBlocked
    ? (accStatus === 'BANNED' ? 'Conta BANIDA' : 'Conta BLOQUEADA') + ' — disparos não permitidos'
    : 'Conta com qualidade baixa - disparos bloqueados';

  const fetchCampaigns = useCallback(async () => {
    if (!accessToken) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/messaging/campaigns?wabaAccountId=${wabaAccountId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const data = await response.json();

      if (data.success) {
        // Garantir que os dados das campanhas incluem todas as propriedades necessárias
        const campaignsWithDetails = (data.data.campaigns || []).map((campaign: any) => ({
          ...campaign,
          templateName: campaign.templateName,
          templateLanguage: campaign.templateLanguage,
          wabaAccountId: campaign.wabaAccountId,
        }));
        setCampaigns(campaignsWithDetails);
      } else {
        setError(data.message || 'Erro ao carregar campanhas');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar campanhas');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, wabaAccountId]);

  const fetchTemplates = useCallback(async () => {
    if (!accessToken) return;

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/templates?wabaAccountId=${wabaAccountId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const data = await response.json();

      if (data.success) {
        // Filtrar apenas templates aprovados
        const allTemplates = data.data.templates || [];
        const approvedTemplates = allTemplates.filter(
          (t: Template) => t.status === 'APPROVED' || t.status.startsWith('ACTIVE')
        );
        setTemplates(approvedTemplates);
      }
    } catch (err) {
      console.error('Erro ao buscar templates:', err);
    }
  }, [accessToken, wabaAccountId]);

  useEffect(() => {
    fetchCampaigns();
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // WebSocket para atualizações em tempo real
  const handleCampaignUpdate = useCallback((campaign: Campaign) => {
    setCampaigns((prev) => {
      const index = prev.findIndex((c) => c.id === campaign.id);
      if (index >= 0) {
        // Atualizar campanha existente
        const updated = [...prev];
        updated[index] = campaign;
        return updated;
      } else {
        // Adicionar nova campanha
        return [campaign, ...prev];
      }
    });
  }, []);

  const handleCampaignCreated = useCallback((campaign: Campaign) => {
    setCampaigns((prev) => [campaign, ...prev]);
  }, []);

  useWebSocket({
    onCampaignUpdate: handleCampaignUpdate,
    onCampaignCreated: handleCampaignCreated,
    enabled: !!accessToken,
  });

  const handleStartCampaign = (campaignId: string) => {
    if (!accessToken) return;

    showConfirm({
      title: 'Iniciar campanha',
      message: 'Deseja realmente iniciar esta campanha?',
      variant: 'warning',
      confirmText: 'Iniciar',
      onConfirm: async () => {
        setActionLoading(campaignId);
        setError(null);

        try {
          const response = await apiClient.startCampaign(campaignId, accessToken);

          if (response.success) {
            setCampaigns((prev) =>
              prev.map((c) => (c.id === campaignId ? { ...c, status: 'ACTIVE' } : c))
            );
          } else {
            setError(response.message || 'Erro ao iniciar campanha');
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Erro ao iniciar campanha');
        } finally {
          setActionLoading(null);
        }
      },
    });
  };

  const handlePauseCampaign = (campaignId: string) => {
    if (!accessToken) return;

    showConfirm({
      title: 'Pausar campanha',
      message: 'Deseja realmente pausar esta campanha?',
      variant: 'warning',
      confirmText: 'Pausar',
      onConfirm: async () => {
        setActionLoading(campaignId);
        setError(null);

        try {
          const response = await apiClient.pauseCampaign(campaignId, accessToken);

          if (response.success) {
            setCampaigns((prev) =>
              prev.map((c) => (c.id === campaignId ? { ...c, status: 'PAUSED' } : c))
            );
          } else {
            setError(response.message || 'Erro ao pausar campanha');
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Erro ao pausar campanha');
        } finally {
          setActionLoading(null);
        }
      },
    });
  };

  const handleCancelCampaign = (campaignId: string) => {
    if (!accessToken) return;

    showConfirm({
      title: 'Cancelar campanha',
      message: 'Deseja realmente cancelar esta campanha? Esta acao nao pode ser desfeita.',
      variant: 'danger',
      confirmText: 'Cancelar campanha',
      onConfirm: async () => {
        setActionLoading(campaignId);
        setError(null);

        try {
          const response = await apiClient.cancelCampaign(campaignId, accessToken);

          if (response.success) {
            setCampaigns((prev) =>
              prev.map((c) => (c.id === campaignId ? { ...c, status: 'CANCELLED' } : c))
            );
          } else {
            setError(response.message || 'Erro ao cancelar campanha');
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Erro ao cancelar campanha');
        } finally {
          setActionLoading(null);
        }
      },
    });
  };

  const getStatusColor = (status: string) => {
    const normalizedStatus = status.toUpperCase();
    const colors: Record<string, string> = {
      DRAFT: 'bg-bg-3 text-text-2',
      SCHEDULED: 'bg-accent-2/10 text-accent-2',
      ACTIVE: 'bg-success/10 text-success',
      PAUSED: 'bg-warning/10 text-warning',
      COMPLETED: 'bg-success/10 text-success',
      CANCELLED: 'bg-text-3/10 text-text-3',
    };
    return colors[normalizedStatus] || 'bg-bg-3 text-text-2';
  };

  const getStatusLabel = (status: string) => {
    const normalizedStatus = status.toUpperCase();
    const labels: Record<string, string> = {
      DRAFT: 'Rascunho',
      SCHEDULED: 'Agendada',
      ACTIVE: 'Ativa',
      PAUSED: 'Pausada',
      COMPLETED: 'Concluída',
      CANCELLED: 'Cancelada',
    };
    return labels[normalizedStatus] || status;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <p className="text-text-2">Carregando campanhas...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-text-1">Campanhas</h2>
          <p className="text-text-3 text-sm mt-1">
            Gerencie suas campanhas de disparo
          </p>
        </div>
        <button
          onClick={() => {
            setEditingCampaign(null); // Garantir que não está em modo de edição
            setShowCreateModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-accent-1 hover:bg-accent-2 text-white rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-accent-1 focus:ring-offset-2 focus:ring-offset-bg-1"
        >
          <Plus size={18} />
          Nova Campanha
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-danger/10 border border-danger rounded-md p-4">
          <p className="text-danger text-sm">{error}</p>
        </div>
      )}

      {/* Empty State */}
      {campaigns.length === 0 && !error ? (
        <div className="bg-bg-2 border border-bg-3 rounded-lg shadow-md p-12 text-center">
          <Send className="mx-auto mb-4 text-text-3" size={48} />
          <h3 className="text-xl font-semibold text-text-1 mb-2">
            Nenhuma campanha criada
          </h3>
          <p className="text-text-2 mb-6">
            Crie sua primeira campanha para começar a enviar mensagens em massa
          </p>
          <button
            onClick={() => {
              setEditingCampaign(null); // Garantir que não está em modo de edição
              setShowCreateModal(true);
            }}
            className="inline-flex items-center gap-2 px-6 py-3 bg-accent-1 hover:bg-accent-2 text-white rounded-md transition-colors"
          >
            <Plus size={18} />
            Criar Primeira Campanha
          </button>
        </div>
      ) : (
        /* Campaigns List */
        <div className="space-y-4">
          {campaigns.map((campaign) => (
            <div
              key={campaign.id}
              className="bg-bg-2 border border-bg-3 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-text-1">
                      {campaign.name}
                    </h3>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}
                    >
                      {getStatusLabel(campaign.status)}
                    </span>
                  </div>
                  <p className="text-text-3 text-sm">
                    Criada em {new Date(campaign.createdAt).toLocaleDateString('pt-BR')}
                    {campaign.scheduledAt && (
                      <span> • Agendada para {new Date(campaign.scheduledAt).toLocaleString('pt-BR')}</span>
                    )}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  {campaign.status.toUpperCase() === 'DRAFT' && (
                    <>
                      <button
                        onClick={() => {
                          setEditingCampaign(campaign);
                          setShowCreateModal(true);
                        }}
                        disabled={actionLoading === campaign.id}
                        className="p-2 bg-accent-1/10 text-accent-1 hover:bg-accent-1/20 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Editar rascunho"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => isDispatchBlocked
                          ? setError('Nao e possivel iniciar campanhas. A conta esta com qualidade BAIXA (LOW). Aguarde a qualidade melhorar.')
                          : handleStartCampaign(campaign.id)
                        }
                        disabled={actionLoading === campaign.id || campaign.totalRecipients === 0 || isDispatchBlocked}
                        className="p-2 bg-success/10 text-success hover:bg-success/20 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={isDispatchBlocked ? blockMessage : 'Iniciar campanha'}
                      >
                        <Play size={16} />
                      </button>
                    </>
                  )}
                  {campaign.status.toUpperCase() === 'SCHEDULED' && (
                    <button
                      onClick={() => isDispatchBlocked
                        ? setError('Nao e possivel iniciar campanhas. A conta esta com qualidade BAIXA (LOW). Aguarde a qualidade melhorar.')
                        : handleStartCampaign(campaign.id)
                      }
                      disabled={actionLoading === campaign.id || isDispatchBlocked}
                      className="p-2 bg-success/10 text-success hover:bg-success/20 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title={isDispatchBlocked ? blockMessage : 'Iniciar campanha agora'}
                    >
                      <Play size={16} />
                    </button>
                  )}
                  {campaign.status.toUpperCase() === 'ACTIVE' && (
                    <>
                      <button
                        onClick={() => handlePauseCampaign(campaign.id)}
                        disabled={actionLoading === campaign.id}
                        className="p-2 bg-warning/10 text-warning hover:bg-warning/20 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Pausar campanha"
                      >
                        <Pause size={16} />
                      </button>
                      <button
                        onClick={() => handleCancelCampaign(campaign.id)}
                        disabled={actionLoading === campaign.id}
                        className="p-2 bg-danger/10 text-danger hover:bg-danger/20 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Cancelar campanha"
                      >
                        <X size={16} />
                      </button>
                    </>
                  )}
                  {campaign.status.toUpperCase() === 'PAUSED' && (
                    <>
                      <button
                        onClick={() => isDispatchBlocked
                          ? setError('Nao e possivel retomar campanhas. A conta esta com qualidade BAIXA (LOW). Aguarde a qualidade melhorar.')
                          : handleStartCampaign(campaign.id)
                        }
                        disabled={actionLoading === campaign.id || isDispatchBlocked}
                        className="p-2 bg-success/10 text-success hover:bg-success/20 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={isDispatchBlocked ? blockMessage : 'Retomar campanha'}
                      >
                        <Play size={16} />
                      </button>
                      <button
                        onClick={() => handleCancelCampaign(campaign.id)}
                        disabled={actionLoading === campaign.id}
                        className="p-2 bg-danger/10 text-danger hover:bg-danger/20 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Cancelar campanha"
                      >
                        <X size={16} />
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => navigate(`/waba/${wabaAccountId}/campaigns/${campaign.id}`)}
                    className="p-2 bg-accent-1/10 text-accent-1 hover:bg-accent-1/20 rounded-md transition-colors"
                    title="Ver detalhes da campanha"
                  >
                    <BarChart3 size={16} />
                  </button>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-bg-3 rounded-md p-3">
                  <p className="text-text-3 text-xs mb-1">Total</p>
                  <p className="text-text-1 text-lg font-semibold">
                    {campaign.totalRecipients}
                  </p>
                </div>
                <div className="bg-accent-1/10 rounded-md p-3">
                  <p className="text-text-3 text-xs mb-1">Enviadas</p>
                  <p className="text-accent-1 text-lg font-semibold">
                    {campaign.sentCount}
                  </p>
                </div>
                <div className="bg-success/10 rounded-md p-3">
                  <p className="text-text-3 text-xs mb-1">Entregues</p>
                  <p className="text-success text-lg font-semibold">
                    {campaign.deliveredCount}
                  </p>
                </div>
                <div className="bg-danger/10 rounded-md p-3">
                  <p className="text-text-3 text-xs mb-1">Falhas</p>
                  <p className="text-danger text-lg font-semibold">
                    {campaign.failedCount}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Campaign Modal */}
      <CreateCampaignModal
        wabaAccountId={wabaAccountId}
        templates={templates}
        isOpen={showCreateModal}
        editingCampaign={editingCampaign}
        qualityRating={qualityRating}
        accountStatus={accountStatus}
        onClose={() => {
          setShowCreateModal(false);
          // Aguardar um pouco antes de limpar para garantir que o modal fechou
          setTimeout(() => {
            setEditingCampaign(null);
          }, 300);
        }}
        onSuccess={() => {
          setShowCreateModal(false);
          setTimeout(() => {
            setEditingCampaign(null);
          }, 300);
          fetchCampaigns(); // Refresh list
        }}
      />

      <ConfirmDialog {...dialogProps} />
    </div>
  );
}
